'use client';

import * as React from 'react';

import { CancelRounded, PlayArrow } from '@mui/icons-material';
import {
  DialogTitle,
  IconButton,
  DialogContent,
  Typography,
  DialogActions,
  Dialog,
  Button,
  Box,
  Chip,
} from '@mui/material';
import { EventBus } from '@/shared/utils';
import { ModalType } from '../../../types/modals';
import { LessonProgresses } from '../../../types/lessons';
import { useSeriesByIdStore } from '../../../store/series-by-id-store';
import { SeriesByIdEventKeys, SeriesByIdEvents } from '../../../events';
import { routes } from 'src/shared/utils/routes';
import { formatDuration } from '../../../utils/return-duration-from-seconds';

interface LessonCompletionDialogProps {
  eventBus: EventBus<SeriesByIdEvents>;
  seriesDocumentId: string;
  serieName: string;
}

const LessonCompletionDialog: React.FC<LessonCompletionDialogProps> = ({
  eventBus,
  seriesDocumentId,
  serieName,
}) => {
  const { lessonProgress } = useSeriesByIdStore();

  const [openDialog, setOpenDialog] = React.useState<ModalType | null>(null);

  const handleWatchedLesson = (
    lessonDocumentId: string,
    value: LessonProgresses,
    seriesDocumentId?: string,
  ) => {
    const newValue: LessonProgresses & { seriesDocumentId?: string } =
      value.progress_status === 'completed'
        ? {
            // update case
            documentId: value.documentId || lessonDocumentId,
            progress_status: 'in_progress',
            seriesDocumentId: undefined,
            current_second: 0,
          }
        : {
            // create case
            documentId: value.documentId || lessonDocumentId,
            progress_status: 'completed',
            seriesDocumentId,
            current_second: 0,
          };
    // if the lesson has not been watched yet, create a new lesson progress
    if (!value.documentId) {
      eventBus.emit(SeriesByIdEventKeys.lessonProgresses.create, newValue);
      return;
    }
    eventBus.emit(SeriesByIdEventKeys.lessonProgresses.update, newValue);
  };

  React.useEffect(() => {
    const handleVideoLessonDialog = (data: ModalType | null) => {
      setOpenDialog(data);
    };

    eventBus.on(SeriesByIdEventKeys.modals.lessonCompletionModal, handleVideoLessonDialog);

    return () => {
      eventBus.off(SeriesByIdEventKeys.modals.lessonCompletionModal, handleVideoLessonDialog);
    };
  }, [eventBus]);

  return (
    <Dialog open={!!openDialog} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
      <DialogTitle variant="h5" component="h5" className="flex items-center justify-between p-6">
        Vuoi segnare come {openDialog?.isWatched ? 'da vedere' : 'completata'}?
        <IconButton aria-label="close" onClick={() => setOpenDialog(null)}>
          <CancelRounded className="text-action-divider" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" alignItems="center" className="p-4 bg-background" borderRadius={3}>
          <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto items-center">
            <div className="overflow-hidden rounded-md">
              <img
                loading="lazy"
                src={openDialog?.imageSrc || ''}
                alt=""
                className="object-contain aspect-[1.68] w-[100px] md:w-[150px] rounded-2xl"
              />
            </div>

            <div className="space-y-2">
              <Typography variant="h5" color="textPrimary">
                {openDialog?.title}
              </Typography>
              <Chip
                label={
                  <Typography variant="button-s">
                    {formatDuration(openDialog?.duration ?? 0)}
                  </Typography>
                }
                size="medium"
                variant="filled"
                className="bg-paper"
              />
            </div>
          </div>
        </Box>
      </DialogContent>
      <DialogActions className="flex flex-wrap gap-2">
        <Button
          onClick={() => {
            if (!openDialog) return;
            handleWatchedLesson(
              openDialog?.lessonDocumentId,
              openDialog?.lessonProgress,
              seriesDocumentId,
            );
          }}
          disabled={lessonProgress.isLoading}
          size="medium"
          color="primary"
          variant="outlined"
          className="w-full sm:w-auto"
        >
          <Typography variant="button-m">
            {openDialog?.isWatched ? 'Segna come da vedere' : 'Segna come completata'}
          </Typography>
        </Button>
        <Button
          onClick={() => {
            setOpenDialog(null);
            if (!openDialog?.title || !openDialog?.documentId) return;
            eventBus.emit(SeriesByIdEventKeys.redirects.to, {
              url: routes.serieLesson.makeUrl(
                seriesDocumentId,
                serieName,
                openDialog?.title,
                openDialog?.documentId,
              ),
              isExternalPage: false,
            });
          }}
          size="medium"
          color="primary"
          variant="contained"
          className="w-full sm:w-auto space-x-2"
        >
          <PlayArrow className="size-4" />
          <Typography variant="button-m">Guarda Lezione</Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { LessonCompletionDialog };
